import { RRule, Frequency } from 'rrule';
import type { PlannerEvent, RecurrenceType } from '../types/planner';
import { parseISO, differenceInDays } from 'date-fns';

const FREQ_MAP: Partial<Record<RecurrenceType, Frequency>> = {
  daily:    RRule.DAILY,
  weekdays: RRule.WEEKLY,
  weekly:   RRule.WEEKLY,
  biweekly: RRule.WEEKLY,
  monthly:  RRule.MONTHLY,
  yearly:   RRule.YEARLY,
  custom:   RRule.WEEKLY,
};

// RRule weekday constants indexed by JS day (0=Sun … 6=Sat)
const RRULE_WEEKDAYS = [
  RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA,
];

/**
 * Expands a single event into multiple occurrences if it has a recurrence rule.
 * The expansion happens between the `from` and `to` dates.
 */
export function expandEventOccurrences(event: PlannerEvent, from: Date, to: Date): PlannerEvent[] {
  const effectiveEndDate = event.endDate || event.startDate;
  if (!event.recurrence || event.recurrence === 'none') {
    const start = parseISO(event.startDate);
    const end = parseISO(effectiveEndDate);
    if (start <= to && end >= from) return [{ ...event, endDate: effectiveEndDate }];
    return [];
  }

  const isBirthdayOrAnniv = (event.type === 'birthday' || (event.title || '').toLowerCase().includes('birthday') || (event.title || '').toLowerCase().includes('bday') || (event.type as string) === 'anniversary' || (event.title || '').toLowerCase().includes('anniversary'));
  const effectiveRecurrence = (isBirthdayOrAnniv && ['weekly', 'biweekly', 'daily', 'weekdays'].includes(event.recurrence))
    ? 'yearly'
    : event.recurrence;

  const freq = FREQ_MAP[effectiveRecurrence];
  if (!freq) return [{ ...event, endDate: effectiveEndDate }];

  // Parse YYYY-MM-DD as UTC 12:00:00 to prevent local timezone offsets from shifting UTC dates
  const parseCleanUTC = (isoStr: string) => {
    const cleanDate = isoStr.split('T')[0];
    const [y, m, d] = cleanDate.split('-').map(Number);
    return new Date(Date.UTC(y, (m || 1) - 1, d || 1, 12, 0, 0));
  };

  const startDate = parseCleanUTC(event.startDate);
  const durationDays = Math.max(0, differenceInDays(parseISO(effectiveEndDate), parseISO(event.startDate)) || 0);

  // Respect recurrenceEndDate if set
  const until = event.recurrenceEndDate
    ? parseCleanUTC(event.recurrenceEndDate)
    : new Date(Date.UTC(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59));

  const fromUTC = new Date(Date.UTC(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0));
  const searchFromUTC = new Date(fromUTC.getTime() - durationDays * 86400000);

  const ruleOptions: ConstructorParameters<typeof RRule>[0] = {
    freq,
    dtstart: startDate,
    until,
  };

  switch (event.recurrence) {
    case 'weekdays':
      ruleOptions.byweekday = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR];
      break;
    case 'biweekly':
      ruleOptions.interval = 2;
      break;
    case 'custom':
      if (event.recurrenceDays && event.recurrenceDays.length > 0) {
        ruleOptions.byweekday = event.recurrenceDays.map(d => RRULE_WEEKDAYS[d]);
      }
      break;
  }

  const rule = new RRule(ruleOptions);
  const occurrences = rule.between(searchFromUTC, until, true);

  return occurrences
    .map((date) => {
      // Format as YYYY-MM-DD in UTC so timezone offset never shifts it to the next day
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const endDateObj = new Date(date.getTime() + durationDays * 86400000);
      const endYear = endDateObj.getUTCFullYear();
      const endMonth = String(endDateObj.getUTCMonth() + 1).padStart(2, '0');
      const endDay = String(endDateObj.getUTCDate()).padStart(2, '0');
      const endDateStr = `${endYear}-${endMonth}-${endDay}`;

      return {
        ...event,
        id: `${event.id}_${dateStr}`,
        startDate: dateStr,
        endDate: endDateStr,
      };
    })
    .filter(occurrence => {
      if (!event.excludedDates) return true;
      return !event.excludedDates.includes(occurrence.startDate);
    });
}

/** Returns a human-readable label for a recurrence type + optional custom days */
export function recurrenceLabel(
  recurrence: RecurrenceType | undefined,
  recurrenceDays?: number[],
): string {
  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  switch (recurrence) {
    case 'daily':    return 'Every day';
    case 'weekdays': return 'Weekdays (Mon–Fri)';
    case 'weekly':   return 'Every week';
    case 'biweekly': return 'Every 2 weeks';
    case 'monthly':  return 'Every month';
    case 'yearly':   return 'Every year';
    case 'custom':
      if (recurrenceDays && recurrenceDays.length > 0) {
        return recurrenceDays.sort().map(d => DAY_NAMES[d]).join(', ');
      }
      return 'Custom';
    default: return 'Does not repeat';
  }
}

/** Converts internal recurrence type to Google Calendar RRULE string */
export function getGoogleRecurrenceRule(
  recurrence: RecurrenceType | undefined,
  recurrenceDays?: number[]
): string | undefined {
  if (!recurrence || recurrence === 'none') return undefined;

  switch (recurrence) {
    case 'daily':    return 'FREQ=DAILY';
    case 'weekdays': return 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR';
    case 'weekly':   return 'FREQ=WEEKLY';
    case 'biweekly': return 'FREQ=WEEKLY;INTERVAL=2';
    case 'monthly':  return 'FREQ=MONTHLY';
    case 'yearly':   return 'FREQ=YEARLY';
    case 'custom':
      if (recurrenceDays && recurrenceDays.length > 0) {
        const RRULE_DAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
        const days = recurrenceDays.map(d => RRULE_DAYS[d]).join(',');
        return `FREQ=WEEKLY;BYDAY=${days}`;
      }
      return 'FREQ=WEEKLY';
    default: return undefined;
  }
}

/** 
 * Parses Google Calendar RRULE strings back to internal recurrence format.
 * Returns { recurrence: RecurrenceType, recurrenceDays?: number[] }
 */
export function parseGoogleRecurrence(recurrenceStrings: string[] | undefined): { 
  recurrence: RecurrenceType; 
  recurrenceDays?: number[];
  until?: string;
  excludedDates?: string[];
} {
  if (!recurrenceStrings || recurrenceStrings.length === 0) {
    return { recurrence: 'none', excludedDates: [] };
  }

  // Parse EXDATE lines if present
  const excludedDates: string[] = [];
  recurrenceStrings.forEach(s => {
    if (s.startsWith('EXDATE:') || s.startsWith('EXDATE;')) {
      const colonIdx = s.indexOf(':');
      if (colonIdx !== -1) {
        const val = s.slice(colonIdx + 1);
        // Excludes could be a comma-separated list of dates/times (e.g. 20260805T100000Z,20260806T100000Z)
        const parts = val.split(',');
        parts.forEach(part => {
          const clean = part.trim();
          if (clean.length >= 8) {
            const y = clean.substring(0, 4);
            const m = clean.substring(4, 6);
            const d = clean.substring(6, 8);
            excludedDates.push(`${y}-${m}-${d}`);
          }
        });
      }
    }
  });

  const rruleStr = recurrenceStrings.find(s => s.startsWith('RRULE:')) || (recurrenceStrings[0]?.startsWith('EXDATE') ? undefined : recurrenceStrings[0]);
  if (!rruleStr) {
    return { recurrence: 'none', excludedDates };
  }
  const parts = rruleStr.replace('RRULE:', '').split(';');
  const params: Record<string, string> = {};
  
  parts.forEach(p => {
    const [key, value] = p.split('=');
    if (key && value) params[key.toUpperCase()] = value.toUpperCase();
  });

  const freq = params['FREQ'];
  const interval = parseInt(params['INTERVAL'] || '1', 10);
  const byDay = params['BYDAY'];
  const untilStr = params['UNTIL'];

  let untilDate: string | undefined;
  if (untilStr) {
    // UNTIL is usually YYYYMMDDTHHMMSSZ
    untilDate = `${untilStr.substring(0, 4)}-${untilStr.substring(4, 6)}-${untilStr.substring(6, 8)}`;
  }

  if (freq === 'DAILY') return { recurrence: 'daily', until: untilDate };
  if (freq === 'MONTHLY') return { recurrence: 'monthly', until: untilDate };
  if (freq === 'YEARLY') return { recurrence: 'yearly', until: untilDate };
  
  if (freq === 'WEEKLY') {
    if (interval === 2) return { recurrence: 'biweekly', until: untilDate };
    
    if (byDay) {
      if (byDay === 'MO,TU,WE,TH,FR') return { recurrence: 'weekdays', until: untilDate };
      
      const dayMap: Record<string, number> = { 'SU': 0, 'MO': 1, 'TU': 2, 'WE': 3, 'TH': 4, 'FR': 5, 'SA': 6 };
      const days = byDay.split(',').map(d => dayMap[d]).filter(d => d !== undefined);
      return { recurrence: 'custom', recurrenceDays: days, until: untilDate };
    }
    
    return { recurrence: 'weekly', until: untilDate };
  }

  return { recurrence: 'none' };
}
